import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getHeroList, getGwDeckList, getGwCounterDetail, saveGwCounter } from '../../api/senaApi';
import { heroPortrait, skillImg as senaSkillImg, skillLabel } from './senaAssets';
import './SenaGuideWrite.css';
import './SenaGwCounterWrite.css';

const MAX_SKILLS_PER_CASE = 3;

const skillImg = (heroNm, skillNo) => senaSkillImg(heroNm, 'ALLY', skillNo);

export default function SenaGwCounterWrite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const deckId = params.get('deckId');
  const counterId = params.get('counterId'); // 기존 카운터에 스킬 케이스 추가 시
  const userId = localStorage.getItem('userId') || '게스트';

  const [allyHeroes, setAllyHeroes] = useState([]);
  const [deckInfo, setDeckInfo] = useState(null);

  // 카운터 영웅 3슬롯
  const [selected, setSelected] = useState([null, null, null]);
  const [searches, setSearches] = useState(['', '', '']);
  const [notes, setNotes] = useState(['', '', '']);   // 영웅 슬롯별 비고
  const [description, setDescription] = useState('');
  const [isExisting, setIsExisting] = useState(false); // 기존 카운터 수정 모드

  // 스킬 케이스
  const [skillCases, setSkillCases] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getHeroList('ALLY').then(r => setAllyHeroes(r.data || [])).catch(() => {});
  }, []);

  // 기존 카운터 로드 (스킬 케이스 추가 모드)
  useEffect(() => {
    if (counterId) {
      getGwCounterDetail(Number(counterId)).then(r => {
        const c = r.data;
        if (!c) return;
        setIsExisting(true);
        setDescription(c.description || '');
        setNotes([c.note1 || '', c.note2 || '', c.note3 || '']);
        setSkillCases((c.skillCases || []).map(sc => ({
          allySkills: sc.allySkills || '',
          description: sc.description || '',
        })));
        // deckId from counter
        if (c.deckId) {
          getGwDeckList().then(r2 => {
            const deck = (r2.data || []).find(d => d.deckId === c.deckId);
            setDeckInfo(deck || null);
          }).catch(() => {});
        }
      }).catch(() => {});
    } else if (deckId) {
      getGwDeckList().then(r => {
        const deck = (r.data || []).find(d => d.deckId === Number(deckId));
        setDeckInfo(deck || null);
      }).catch(() => {});
    }
  }, [deckId, counterId]);

  // 기존 카운터의 영웅 세팅
  useEffect(() => {
    if (counterId && allyHeroes.length > 0) {
      getGwCounterDetail(Number(counterId)).then(r => {
        const c = r.data;
        if (!c) return;
        const h1 = allyHeroes.find(h => h.heroId === c.hero1Id);
        const h2 = allyHeroes.find(h => h.heroId === c.hero2Id);
        const h3 = allyHeroes.find(h => h.heroId === c.hero3Id);
        setSelected([h1 || null, h2 || null, h3 || null]);
      }).catch(() => {});
    }
  }, [counterId, allyHeroes]);

  const handleSelect = (slotIdx, hero) => {
    setSelected(prev => { const next = [...prev]; next[slotIdx] = hero; return next; });
    setSearches(prev => { const next = [...prev]; next[slotIdx] = ''; return next; });
  };

  const handleRemove = (slotIdx) => {
    setSelected(prev => { const next = [...prev]; next[slotIdx] = null; return next; });
  };

  // 아군 카운터 영웅의 스킬 목록
  const allyHeroSkills = selected.filter(Boolean);

  // 케이스 추가 (적 스킬 유무는 구분하지 않는다 — 항상 '적 스킬 모를 때' 기준)
  const addCase = () => {
    setSkillCases(prev => [...prev, { allySkills: '', description: '' }]);
  };

  const updateCase = (idx, field, value) => {
    setSkillCases(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const removeCase = (idx) => {
    setSkillCases(prev => prev.filter((_, i) => i !== idx));
  };

  // 스킬 토글 (순서 기반, 최대 3개)
  const toggleSkillInCase = (idx, field, skillId) => {
    setSkillCases(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const ids = (c[field] || '').split(',').filter(Boolean);
      const strId = String(skillId);
      if (ids.includes(strId)) {
        // 제거
        return { ...c, [field]: ids.filter(id => id !== strId).join(',') };
      } else {
        // 추가 (최대 3개)
        if (ids.length >= MAX_SKILLS_PER_CASE) return c;
        return { ...c, [field]: [...ids, strId].join(',') };
      }
    }));
  };

  const getSkillOrder = (caseIdx, field, skillId) => {
    const ids = (skillCases[caseIdx]?.[field] || '').split(',').filter(Boolean);
    const idx = ids.indexOf(String(skillId));
    return idx >= 0 ? idx + 1 : 0;
  };

  // 저장
  const handleSave = async () => {
    if (!isExisting && selected.filter(Boolean).length < 3) {
      alert('카운터 영웅 3명을 모두 선택해주세요.');
      return;
    }

    setSaving(true);
    try {
      await saveGwCounter({
        counterId: counterId ? Number(counterId) : undefined,
        deckId: Number(deckId || deckInfo?.deckId),
        hero1Id: selected[0]?.heroId,
        hero2Id: selected[1]?.heroId,
        hero3Id: selected[2]?.heroId,
        authorNm: userId,
        description: description || null,
        note1: notes[0] || null,
        note2: notes[1] || null,
        note3: notes[2] || null,
        skillCases: skillCases
          .filter(sc => (sc.allySkills || '').length > 0)
          .map((sc, i) => ({
            caseType: 'UNKNOWN',
            caseOrder: i + 1,
            enemySkills: null,
            allySkills: sc.allySkills,
            description: sc.description || null,
          })),
      });
      alert('저장되었습니다.');
      navigate('/sena/guild-war');
    } catch (e) {
      alert(e?.response?.data || '저장에 실패했습니다.');
    } finally { setSaving(false); }
  };

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate(-1)}>← 뒤로</button>
        <button className="siege-home-btn" onClick={() => navigate('/sena')}>홈</button>
        <h1 className="sena-header-guild">{isExisting ? '스킬 케이스 수정' : '카운터 등록'}</h1>
        <p className="sena-header-sub">길드전</p>
      </header>

      <div className="gw-container">
        {/* 적군 덱 정보 */}
        <section className="gw-section">
          <h3 className="gw-section-title">적군 덱</h3>
          {deckInfo ? (
            <div className="gw-enemy-tags">
              <span className="gw-tag enemy">{deckInfo.hero1Nm}</span>
              <span className="gw-tag enemy">{deckInfo.hero2Nm}</span>
              <span className="gw-tag enemy">{deckInfo.hero3Nm}</span>
            </div>
          ) : <p className="gw-hint">덱 정보를 불러오는 중...</p>}
        </section>

        {/* 카운터 영웅 선택 */}
        <section className="gw-section">
          <h3 className="gw-section-title">카운터 영웅 3명</h3>
          <div className="gw-ally-slots">
            {selected.map((hero, idx) => (
              <div key={idx} className={`gw-ally-slot ${hero ? 'filled' : ''}`}>
                {hero ? (
                  <>
                    <span className="gw-ally-name">{hero.heroNm}</span>
                    {!isExisting && <button className="gw-ally-remove" onClick={() => handleRemove(idx)}>×</button>}
                  </>
                ) : <span className="gw-ally-empty">슬롯 {idx + 1}</span>}
              </div>
            ))}
          </div>
          {selected.some(Boolean) && (
            <div className="gwc-note-list">
              <p className="gw-hint">영웅별 비고 (세트옵션·치확·약확·속공 등 세팅 메모)</p>
              {selected.map((hero, idx) => hero && (
                <div key={idx} className="gwc-note-row">
                  <span className="gwc-note-hero">
                    {heroPortrait(hero.heroNm, 'ALLY') && (
                      <img className="gwc-note-portrait" src={heroPortrait(hero.heroNm, 'ALLY')} alt="" />
                    )}
                    {hero.heroNm}
                  </span>
                  <input className="gw-input gwc-note-input" maxLength={200}
                    placeholder="예) 복수자 세트 / 치확 80 / 속공 2순위"
                    value={notes[idx]}
                    onChange={e => setNotes(prev => { const next = [...prev]; next[idx] = e.target.value; return next; })} />
                </div>
              ))}
            </div>
          )}

          {!isExisting && selected.map((hero, idx) => !hero && (
            <div key={idx} className="gw-hero-search-wrap" style={{ marginBottom: 10 }}>
              <input className="gw-hero-search" placeholder={`슬롯 ${idx + 1} 영웅 검색...`}
                value={searches[idx]}
                onChange={e => { const next = [...searches]; next[idx] = e.target.value; setSearches(next); }}
              />
              {searches[idx].length > 0 && (
                <div className="gw-hero-dropdown">
                  {allyHeroes.filter(h => h.heroNm.includes(searches[idx]))
                    .filter(h => !selected.some(s => s && s.heroId === h.heroId))
                    .map(h => (
                      <button key={h.heroId} className="gw-hero-dropdown-item" onClick={() => handleSelect(idx, h)}>{h.heroNm}</button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </section>

        {/* 설명 */}
        <section className="gw-section">
          <h3 className="gw-section-title">설명 (선택)</h3>
          <textarea className="gw-textarea" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="카운터 덱에 대한 설명..." rows={3} />
        </section>

        {/* 스킬 경우의수 */}
        {(isExisting || selected.filter(Boolean).length === 3) && (
          <section className="gw-section">
            <h3 className="gw-section-title">스킬 경우의수 (케이스당 최대 {MAX_SKILLS_PER_CASE}개)</h3>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button className="gw-toggle-btn active" onClick={addCase}>+ 케이스 추가</button>
            </div>

            {skillCases.map((sc, idx) => (
              <div key={idx} className="gwc-case-card">
                <div className="gwc-case-header">
                  <span className="gw-tag unknown">케이스 {idx + 1}</span>
                  <button className="gw-ally-remove" style={{ position: 'static' }} onClick={() => removeCase(idx)}>×</button>
                </div>

                {/* 아군 스킬 */}
                <div className="gwc-skill-section">
                  <p className="gw-hint">사용할 스킬 (클릭 순서대로, 최대 {MAX_SKILLS_PER_CASE}개):</p>
                  <div className="gwc-skill-btns">
                    {allyHeroSkills.flatMap(hero => (hero.skills || []).map(sk => {
                      const order = getSkillOrder(idx, 'allySkills', sk.skillId);
                      const img = skillImg(hero.heroNm, sk.skillNo);
                      return (
                        <button key={sk.skillId}
                          className={`gwc-skill-btn ${order > 0 ? 'ally selected' : ''}`}
                          onClick={() => toggleSkillInCase(idx, 'allySkills', sk.skillId)}>
                          {order > 0 && <span className="gwc-skill-order">{order}</span>}
                          {img && <img className="gwc-skill-btn-img" src={img} alt="" />}
                          <span className="gwc-skill-btn-text">{hero.heroNm} {skillLabel(sk.skillNo)}</span>
                        </button>
                      );
                    }))}
                  </div>
                </div>

                <input className="gw-input" placeholder="케이스 설명 (선택)"
                  value={sc.description} onChange={e => updateCase(idx, 'description', e.target.value)} />
              </div>
            ))}
          </section>
        )}

        <div className="gw-footer">
          <button className="gw-btn" onClick={() => navigate(-1)}>취소</button>
          <button className="gw-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
